import {fabric} from 'fabric';
import {useCallback} from 'react';
import {deriveIdFromCanvasObject} from '~/CreateFabricInvitation/utils/canvas';

export const useFabricImage = (canvas) => {
  const centerAndScaleImage = useCallback(
    (image, originalObject, options = {}) => {
      if (!canvas || !image || !originalObject) return null;

      const {
        makeSelectable = true,
        resetRotation = true,
        fireEvents = [],
        clipPath = null,
      } = options;

      const scale = fabric.util.findScaleToCover(image, originalObject);

      const centerX = originalObject.left + (originalObject.width * originalObject.scaleX) / 2;
      const centerY = originalObject.top + (originalObject.height * originalObject.scaleY) / 2;

      image.scale(scale).set({
        left: centerX,
        top: centerY,
        originX: 'center',
        originY: 'center',
        centeredRotation: true,
        selectable: makeSelectable,
        hasControls: true,
        _originalObject: originalObject,
      });

      if (clipPath) {
        // eslint-disable-next-line no-param-reassign
        image.clipPath = clipPath;
      }

      if (resetRotation) {
        image.rotate(0);
      }

      canvas.setActiveObject(image);
      canvas.renderAll();

      fireEvents.forEach((event) => {
        if (typeof event === 'string') {
          canvas.fire(event);
        } else if (typeof event === 'object' && event.name) {
          canvas.fire(event.name, event.data || {});
        }
      });

      return scale;
    },
    [canvas]
  );

  const createImageContainer = useCallback(
    (originalObject, id) =>
      new fabric.Rect({
        left: originalObject.left,
        top: originalObject.top,
        id,
        width: originalObject.width,
        height: originalObject.height,
        scaleX: originalObject.scaleX,
        scaleY: originalObject.scaleY,
        fill: 'white',
        selectable: false,
        hasControls: false,
      }),
    []
  );

  const createImageClipPath = useCallback(
    (originalObject, img) =>
      new fabric.Rect({
        left: originalObject.left - img.left,
        top: originalObject.top - img.top,
        width: originalObject.width * originalObject.scaleX,
        height: originalObject.height * originalObject.scaleY,
        originX: 'left',
        originY: 'top',
        absolutePositioned: true,
      }),
    []
  );

  const resetImage = useCallback(
    (image) => {
      if (!canvas || !image || !image._originalObject) return undefined;

      return centerAndScaleImage(image, image._originalObject, {
        fireEvents: ['object:modified', {name: 'object:reseted:uyo', data: {scale: 1}}],
      });
    },
    [canvas, centerAndScaleImage]
  );

  const findUYOImageById = useCallback(
    (id) => {
      if (!canvas) return null;
      return canvas
        .getObjects()
        .find((obj) => obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
    },
    [canvas]
  );

  const sendRectsToBack = useCallback(() => {
    if (!canvas) return;

    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'rect') {
        canvas.sendToBack(obj);
      }
    });
  }, [canvas]);

  return {
    centerAndScaleImage,
    createImageContainer,
    createImageClipPath,
    resetImage,
    findUYOImageById,
    sendRectsToBack,
  };
};
